import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import hrmPayrollService from '../../services/hrmPayrollService';

const initialState = {
  wageTypes: [],
  compensationPlans: [],
  compensationZones: [],
  compensationBands: [],
  compensationSteps: [],
  compensationGrid: [],
  employeeCompensations: [],
  employeeCompensationComponents: [],
  payrollAreas: [],
  payrollPeriods: [],
  payrollRuns: [],
  payrollRun: null,
  payrollResults: [],
  payrollResult: null,
  taxDeclarations: [],
  taxProjections: [],
  countryConfigs: [],
  statutoryComponents: [],
  taxSlabs: [],
  employeeTaxConfigs: [],
  recurringPayments: [],
  additionalPayments: [],
  payrollGLConfigs: [],
  tenantOverrides: [],
  areaAssignments: [],
  offCyclePayrolls: [],
  offCycleItems: [],
  finalSettlements: [],
  retroCalculations: [],
  postingLogs: [],
  payslipTemplates: [],
  bankFileConfigs: [],
  bankFileGenerations: [],

  isLoading: false,
  error: null,
  pagination: { page: 1, pageSize: 25, totalCount: 0 },
};

// Helper to create standard CRUD thunks
const createCrudThunks = (name, service) => ({
  fetchAll: createAsyncThunk(`hrmPayroll/fetch${name}`, async (params = {}, { rejectWithValue }) => {
    try {
      const response = await service(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || `Failed to fetch ${name}`);
    }
  }),
});

// ── Wage Types ─────────────────────────────────────────────────────────────────
export const fetchWageTypes = createAsyncThunk('hrmPayroll/fetchWageTypes', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getWageTypes(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch wage types'); }
});

// ── Compensation Plans ─────────────────────────────────────────────────────────
export const fetchCompensationPlans = createAsyncThunk('hrmPayroll/fetchCompensationPlans', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getCompensationPlans(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch compensation plans'); }
});

// ── Payroll Areas ──────────────────────────────────────────────────────────────
export const fetchPayrollAreas = createAsyncThunk('hrmPayroll/fetchPayrollAreas', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getPayrollAreas(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch payroll areas'); }
});

// ── Payroll Periods ────────────────────────────────────────────────────────────
export const fetchPayrollPeriods = createAsyncThunk('hrmPayroll/fetchPayrollPeriods', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getPayrollPeriods(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch payroll periods'); }
});

// ── Payroll Runs ───────────────────────────────────────────────────────────────
export const fetchPayrollRuns = createAsyncThunk('hrmPayroll/fetchPayrollRuns', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getPayrollRuns(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch payroll runs'); }
});

export const fetchPayrollRun = createAsyncThunk('hrmPayroll/fetchPayrollRun', async (id, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getPayrollRun(id); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch payroll run'); }
});

// ── Payroll Results ────────────────────────────────────────────────────────────
export const fetchPayrollResults = createAsyncThunk('hrmPayroll/fetchPayrollResults', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getPayrollResults(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch payroll results'); }
});

export const fetchPayrollResult = createAsyncThunk('hrmPayroll/fetchPayrollResult', async (id, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getPayrollResult(id); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch payroll result'); }
});

// ── Tax Declarations ───────────────────────────────────────────────────────────
export const fetchTaxDeclarations = createAsyncThunk('hrmPayroll/fetchTaxDeclarations', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getTaxDeclarations(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch tax declarations'); }
});

// ── Country Configs ────────────────────────────────────────────────────────────
export const fetchCountryConfigs = createAsyncThunk('hrmPayroll/fetchCountryConfigs', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getCountryConfigs(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch country configs'); }
});

// ── Statutory Components ───────────────────────────────────────────────────────
export const fetchStatutoryComponents = createAsyncThunk('hrmPayroll/fetchStatutoryComponents', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getStatutoryComponents(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch statutory components'); }
});

// ── Tax Slabs ──────────────────────────────────────────────────────────────────
export const fetchTaxSlabs = createAsyncThunk('hrmPayroll/fetchTaxSlabs', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getTaxSlabs(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch tax slabs'); }
});

// ── Employee Tax Configs ───────────────────────────────────────────────────────
export const fetchEmployeeTaxConfigs = createAsyncThunk('hrmPayroll/fetchEmployeeTaxConfigs', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getEmployeeTaxConfigs(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch employee tax configs'); }
});

// ── Tax Projections ────────────────────────────────────────────────────────────
export const fetchTaxProjections = createAsyncThunk('hrmPayroll/fetchTaxProjections', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getTaxProjections(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch tax projections'); }
});

// ── Recurring Payments ─────────────────────────────────────────────────────────
export const fetchRecurringPayments = createAsyncThunk('hrmPayroll/fetchRecurringPayments', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getRecurringPayments(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch recurring payments'); }
});

// ── Additional Payments ────────────────────────────────────────────────────────
export const fetchAdditionalPayments = createAsyncThunk('hrmPayroll/fetchAdditionalPayments', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getAdditionalPayments(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch additional payments'); }
});

// ── GL Configs ─────────────────────────────────────────────────────────────────
export const fetchPayrollGLConfigs = createAsyncThunk('hrmPayroll/fetchPayrollGLConfigs', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getPayrollGLConfigs(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch GL configs'); }
});

// ── Compensation Zones ───────────────────────────────────────────────────────
export const fetchCompensationZones = createAsyncThunk('hrmPayroll/fetchCompensationZones', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getCompensationZones(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch compensation zones'); }
});

// ── Compensation Bands ───────────────────────────────────────────────────────
export const fetchCompensationBands = createAsyncThunk('hrmPayroll/fetchCompensationBands', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getCompensationBands(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch compensation bands'); }
});

// ── Compensation Steps ───────────────────────────────────────────────────────
export const fetchCompensationSteps = createAsyncThunk('hrmPayroll/fetchCompensationSteps', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getCompensationSteps(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch compensation steps'); }
});

// ── Compensation Grid ────────────────────────────────────────────────────────
export const fetchCompensationGrid = createAsyncThunk('hrmPayroll/fetchCompensationGrid', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getCompensationGrid(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch compensation grid'); }
});

// ── Employee Compensations ───────────────────────────────────────────────────
export const fetchEmployeeCompensations = createAsyncThunk('hrmPayroll/fetchEmployeeCompensations', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getEmployeeCompensations(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch employee compensations'); }
});

// ── Employee Compensation Components ─────────────────────────────────────────
export const fetchEmployeeCompensationComponents = createAsyncThunk('hrmPayroll/fetchEmployeeCompensationComponents', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getEmployeeCompensationComponents(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch employee compensation components'); }
});

// ── Tenant Overrides ─────────────────────────────────────────────────────────
export const fetchTenantOverrides = createAsyncThunk('hrmPayroll/fetchTenantOverrides', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getTenantOverrides(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch tenant overrides'); }
});

// ── Area Assignments ─────────────────────────────────────────────────────────
export const fetchAreaAssignments = createAsyncThunk('hrmPayroll/fetchAreaAssignments', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getAreaAssignments(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch area assignments'); }
});

// ── Off-Cycle Payrolls ───────────────────────────────────────────────────────
export const fetchOffCyclePayrolls = createAsyncThunk('hrmPayroll/fetchOffCyclePayrolls', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getOffCyclePayrolls(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch off-cycle payrolls'); }
});

// ── Off-Cycle Items ──────────────────────────────────────────────────────────
export const fetchOffCycleItems = createAsyncThunk('hrmPayroll/fetchOffCycleItems', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getOffCycleItems(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch off-cycle items'); }
});

// ── Final Settlements ────────────────────────────────────────────────────────
export const fetchFinalSettlements = createAsyncThunk('hrmPayroll/fetchFinalSettlements', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getFinalSettlements(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch final settlements'); }
});

// ── Retro Calculations ──────────────────────────────────────────────────────
export const fetchRetroCalculations = createAsyncThunk('hrmPayroll/fetchRetroCalculations', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getRetroCalculations(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch retro calculations'); }
});

// ── Posting Logs ─────────────────────────────────────────────────────────────
export const fetchPostingLogs = createAsyncThunk('hrmPayroll/fetchPostingLogs', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getPayrollPostingLogs(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch posting logs'); }
});

// ── Payslip Templates ────────────────────────────────────────────────────────
export const fetchPayslipTemplates = createAsyncThunk('hrmPayroll/fetchPayslipTemplates', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getPayslipTemplates(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch payslip templates'); }
});

// ── Bank File Configs ────────────────────────────────────────────────────────
export const fetchBankFileConfigs = createAsyncThunk('hrmPayroll/fetchBankFileConfigs', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getBankFileConfigs(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch bank file configs'); }
});

// ── Bank File Generations ────────────────────────────────────────────────────
export const fetchBankFileGenerations = createAsyncThunk('hrmPayroll/fetchBankFileGenerations', async (params = {}, { rejectWithValue }) => {
  try { const r = await hrmPayrollService.getBankFileGenerations(params); return r.data; }
  catch (e) { return rejectWithValue(e.response?.data || 'Failed to fetch bank file generations'); }
});

// Helper to build standard list handlers
const buildListHandlers = (builder, thunk, stateKey) => {
  builder
    .addCase(thunk.pending, (state) => { state.isLoading = true; state.error = null; })
    .addCase(thunk.fulfilled, (state, action) => {
      state.isLoading = false;
      state[stateKey] = action.payload.results || action.payload;
      if (action.payload.count !== undefined) state.pagination.totalCount = action.payload.count;
      state.error = null;
    })
    .addCase(thunk.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || `Failed to fetch ${stateKey}`;
    });
};

const hrmPayrollSlice = createSlice({
  name: 'hrmPayroll',
  initialState,
  reducers: {
    clearPayrollError: (state) => { state.error = null; },
    clearPayrollRun: (state) => { state.payrollRun = null; },
    clearPayrollResult: (state) => { state.payrollResult = null; },
  },
  extraReducers: (builder) => {
    buildListHandlers(builder, fetchWageTypes, 'wageTypes');
    buildListHandlers(builder, fetchCompensationPlans, 'compensationPlans');
    buildListHandlers(builder, fetchPayrollAreas, 'payrollAreas');
    buildListHandlers(builder, fetchPayrollPeriods, 'payrollPeriods');
    buildListHandlers(builder, fetchPayrollRuns, 'payrollRuns');
    buildListHandlers(builder, fetchPayrollResults, 'payrollResults');
    buildListHandlers(builder, fetchTaxDeclarations, 'taxDeclarations');
    buildListHandlers(builder, fetchCountryConfigs, 'countryConfigs');
    buildListHandlers(builder, fetchStatutoryComponents, 'statutoryComponents');
    buildListHandlers(builder, fetchTaxSlabs, 'taxSlabs');
    buildListHandlers(builder, fetchEmployeeTaxConfigs, 'employeeTaxConfigs');
    buildListHandlers(builder, fetchTaxProjections, 'taxProjections');
    buildListHandlers(builder, fetchRecurringPayments, 'recurringPayments');
    buildListHandlers(builder, fetchAdditionalPayments, 'additionalPayments');
    buildListHandlers(builder, fetchPayrollGLConfigs, 'payrollGLConfigs');
    buildListHandlers(builder, fetchCompensationZones, 'compensationZones');
    buildListHandlers(builder, fetchCompensationBands, 'compensationBands');
    buildListHandlers(builder, fetchCompensationSteps, 'compensationSteps');
    buildListHandlers(builder, fetchCompensationGrid, 'compensationGrid');
    buildListHandlers(builder, fetchEmployeeCompensations, 'employeeCompensations');
    buildListHandlers(builder, fetchEmployeeCompensationComponents, 'employeeCompensationComponents');
    buildListHandlers(builder, fetchTenantOverrides, 'tenantOverrides');
    buildListHandlers(builder, fetchAreaAssignments, 'areaAssignments');
    buildListHandlers(builder, fetchOffCyclePayrolls, 'offCyclePayrolls');
    buildListHandlers(builder, fetchOffCycleItems, 'offCycleItems');
    buildListHandlers(builder, fetchFinalSettlements, 'finalSettlements');
    buildListHandlers(builder, fetchRetroCalculations, 'retroCalculations');
    buildListHandlers(builder, fetchPostingLogs, 'postingLogs');
    buildListHandlers(builder, fetchPayslipTemplates, 'payslipTemplates');
    buildListHandlers(builder, fetchBankFileConfigs, 'bankFileConfigs');
    buildListHandlers(builder, fetchBankFileGenerations, 'bankFileGenerations');

    // Single payroll run
    builder
      .addCase(fetchPayrollRun.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchPayrollRun.fulfilled, (state, action) => { state.isLoading = false; state.payrollRun = action.payload; })
      .addCase(fetchPayrollRun.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; });

    // Single payroll result
    builder
      .addCase(fetchPayrollResult.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchPayrollResult.fulfilled, (state, action) => { state.isLoading = false; state.payrollResult = action.payload; })
      .addCase(fetchPayrollResult.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; });
  },
});

export const { clearPayrollError, clearPayrollRun, clearPayrollResult } = hrmPayrollSlice.actions;
export default hrmPayrollSlice.reducer;
